import { useState, type FormEvent } from "react";
import { changePassword } from "@/api/auth";
import { Badge, Button, Card, Input } from "@/components";
import { useAuthStore } from "@/state/auth.store";
import { toast } from "@/state/toast.store";
import { useLang } from "@/i18n/LanguageContext";
import { roleLabel } from "@/features/users/roles";

export function AccountPage() {
  const { t, lang, setLang } = useLang();
  const user = useAuthStore((s) => s.user);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError(t("De nieuwe wachtwoorden komen niet overeen.", "The new passwords do not match."));
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      setCurrent(""); setNext(""); setConfirm("");
      toast.ok(t("Wachtwoord gewijzigd.", "Password changed."));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Wijzigen mislukt", "Could not change password"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <div className="wrap">
        <div className="section-head">
          <p className="eyebrow">{t("Account", "Account")}</p>
          <h2>{user.full_name || user.email}</h2>
          <p style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Badge tone="ok">{roleLabel(user.role, t)}</Badge>
            <span style={{ color: "var(--grey-text)", fontFamily: "var(--mono)", fontSize: 13 }}>{user.email}</span>
          </p>
        </div>

        <div className="cards" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <Card>
            <p className="card-num">{t("BEVEILIGING", "SECURITY")}</p>
            <h3>{t("Wachtwoord wijzigen", "Change password")}</h3>
            <form onSubmit={onSubmit}>
              {error && <div className="form-error">{error}</div>}
              <Input label={t("Huidig wachtwoord", "Current password")} name="current" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
              <Input label={t("Nieuw wachtwoord", "New password")} name="new" type="password" autoComplete="new-password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} />
              <Input label={t("Herhaal nieuw wachtwoord", "Repeat new password")} name="confirm" type="password" autoComplete="new-password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              <Button type="submit" disabled={busy}>{busy ? t("Bezig…", "Working…") : t("Wachtwoord opslaan", "Save password")}</Button>
            </form>
          </Card>

          <Card>
            <p className="card-num">{t("VOORKEUREN", "PREFERENCES")}</p>
            <h3>{t("Taal", "Language")}</h3>
            <p style={{ color: "var(--grey-text)", marginBottom: 12 }}>
              {t("Kies de taal van de interface.", "Choose the interface language.")}
            </p>
            <div className="lang-toggle" role="group" aria-label={t("Taal", "Language")}>
              <button type="button" aria-pressed={lang === "nl"} onClick={() => setLang("nl")}>NL</button>
              <button type="button" aria-pressed={lang === "en"} onClick={() => setLang("en")}>EN</button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
